import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { NotebookNote } from '../../../shared/types/notebook'
import { searchNotes } from '../notebookTree'
import PageRow from './PageRow'

interface Props {
  open: boolean
  onClose: () => void
  notes: NotebookNote[]
  onNavigate: (id: string) => void
}

export default function NotebookSearch({ open, onClose, notes, onNavigate }: Props) {
  const { t } = useTranslation('notebook')
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Focus search input when search opens
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [open])

  // Reset query when closing
  const handleClose = () => {
    onClose()
    setSearchQuery('')
  }

  const handleNavigate = (id: string) => {
    onNavigate(id)
    setSearchQuery('')
  }

  const searchResults = searchQuery ? searchNotes(notes, searchQuery) : []

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-30 bg-midnight/98 flex flex-col"
        >
          {/* Search header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gold/10">
            <Search size={16} className="text-pearl/60 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('searchAll')}
              className="flex-1 bg-transparent text-ivory text-sm placeholder:text-pearl/55 focus:outline-none"
            />
            <button
              onClick={handleClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
            >
              <X size={16} className="text-pearl/60" />
            </button>
          </div>
          {/* Search results */}
          <div className="flex-1 overflow-y-auto px-4 py-2">
            {searchQuery && searchResults.length === 0 && (
              <p className="text-pearl/60 text-sm text-center py-8">{t('noResults')}</p>
            )}
            {searchResults.map(note => (
              <PageRow
                key={note.id}
                note={note}
                depth={0}
                hasChildren={false}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
